import React, { memo, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import type { JiraHorizontalNavigationContent$key } from '@atlassian/jira-relay/src/__generated__/JiraHorizontalNavigationContent.graphql';
import { useSetMostRecentlyViewedBoard } from '@atlassian/jira-horizontal-nav-common/src/services/set-most-recently-viewed-board/index.tsx';
import { Nav4OnboardingComponentNames } from '@atlassian/jira-atlassian-onboarding-nav4/src/types.tsx';
import { OnboardingSpotlightMaybe } from '@atlassian/jira-atlassian-onboarding-nav4/src/ui/onboarding-spotlight-maybe/index.tsx';
import { CustomTemplateProjectBanner } from './CustomTemplateProjectBanner.tsx';
import NavHeader from './header/NavHeader.tsx';
import NavWrapper from './NavWrapper.tsx';
import TabsContent from './TabsContent.tsx';
import ThemeEvaluated from './ThemeEvaluated.tsx';

const JiraHorizontalNavigationContent = ({
	queryRef,
}: {
	queryRef: JiraHorizontalNavigationContent$key;
}) => {
	const data = useFragment<JiraHorizontalNavigationContent$key>(
		graphql`
			fragment JiraHorizontalNavigationContent on Query
			@argumentDefinitions(
				isJiraCustomTemplatesUxEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-custom-templates-ux-fg.relayprovider"
				}
			) {
				...TabsContent_query
				...NavHeader_query
				jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $projectIdOrKey)
					@include(if: $withProject) {
					...ThemeEvaluated
					...TabsContent_project
					...NavHeader_project
					...CustomTemplateProjectBanner @include(if: $isJiraCustomTemplatesUxEnabled)
					softwareBoardCount
					__id
				}
				jira {
					containerNavigation(input: $containerNavigationInput)
						@required(action: THROW)
						@optIn(to: "JiraContainerNavigation") {
						... on JiraContainerNavigation {
							scopeId
						}
					}
				}
			}
		`,
		queryRef,
	);

	const project = data.jira_projectByIdOrKey;

	const [isFullscreen] = useIsFullscreen();

	const setMostRecentlyViewedBoard = useSetMostRecentlyViewedBoard();

	useEffect(() => {
		if (
			data?.jira?.containerNavigation?.scopeId &&
			project?.softwareBoardCount &&
			project.softwareBoardCount > 0
		) {
			setMostRecentlyViewedBoard?.(data.jira.containerNavigation.scopeId, project?.__id);
		}
	}, [
		setMostRecentlyViewedBoard,
		data?.jira?.containerNavigation?.scopeId,
		project?.__id,
		project?.softwareBoardCount,
	]);
	return (
		<OnboardingSpotlightMaybe spotlight={Nav4OnboardingComponentNames.TAB_NAV_SPOTLIGHT}>
			{project && fg('jira_custom_templates_ux') && (
				<CustomTemplateProjectBanner projectFragment={project} />
			)}
			<NavWrapper>
				<NavHeader projectFragment={project} queryFragment={data} />

				{!fg('jira_theming_relay_migration') && project && <ThemeEvaluated projectRef={project} />}

				{!isFullscreen && <TabsContent projectFragment={project} queryFragment={data} />}
			</NavWrapper>
		</OnboardingSpotlightMaybe>
	);
};

export default memo(JiraHorizontalNavigationContent);
